import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../Firebase/Config";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import "./AdminChat.css";

function ProjectPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const user = auth.currentUser;

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      const docRef = doc(db, "estimates", projectId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setProject({ id: docSnap.id, ...docSnap.data() });
      }
    };
    fetchProject();
  }, [projectId]);

  // Listen for messages (project-specific)
  useEffect(() => {
    if (!project) return;
    const q = query(
      collection(db, "messages", project.uid, projectId, "messages"),
      orderBy("timestamp")
    );
    const unsub = onSnapshot(q, (querySnapshot) => {
      const msgs = [];
      querySnapshot.forEach((doc) => {
        msgs.push({ id: doc.id, ...doc.data() });
      });
      setMessages(msgs);
    });
    return () => unsub();
  }, [project]);

  // Send a message as admin
  const sendMessage = async (e) => {
    e.preventDefault();
    if (input.trim() === "" || !project) return;
    const senderType = isOwner ? "user" : "admin";
    await addDoc(
      collection(db, "messages", project.uid, projectId, "messages"),
      {
        sender: senderType,
        text: input,
        timestamp: serverTimestamp(),
      }
    );
    setInput("");
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!project) return <div>Loading...</div>;

  // Check if user is owner
  const isOwner = user && project && user.uid === project.uid;

  // Handle edit form
  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, "estimates", projectId), editData);
      setProject({ ...project, ...editData });
      setEditMode(false);
      alert("Project updated!");
    } catch (err) {
      alert("Failed to update project: " + err.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this project?"))
      return;
    try {
      await deleteDoc(doc(db, "estimates", projectId));
      alert("Project deleted.");
      navigate(`/profile/${user.uid}`);
    } catch (err) {
      alert("Failed to delete project: " + err.message);
    }
  };

  return (
    <div
      style={{
        maxWidth: 700,
        margin: "2rem auto",
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 2px 12px #ddd",
        padding: 24,
      }}
    >
      <h1>Project Details</h1>
      {isOwner && (
        <div style={{ marginBottom: 18 }}>
          <button
            onClick={() => {
              setEditData({
                description: project.description,
                phone: project.phone,
                // Add other fields if needed
              });
              setEditMode(true);
            }}
          >
            Edit Project
          </button>
          <button
            onClick={handleDelete}
            style={{ marginLeft: 12, background: "#ef5350", color: "#fff" }}
          >
            Delete Project
          </button>
        </div>
      )}

      {editMode ? (
        <form onSubmit={handleEditSubmit}>
          <label>
            Description:
            <input
              name="description"
              value={editData.description}
              onChange={handleEditChange}
              style={{ width: "100%", marginBottom: 12 }}
            />
          </label>
          <label>
            Phone:
            <input
              name="phone"
              value={editData.phone}
              onChange={handleEditChange}
              style={{ width: "100%", marginBottom: 12 }}
            />
          </label>
          {/* Add more fields as needed */}
          <button type="submit">Save Changes</button>
          <button
            type="button"
            onClick={() => setEditMode(false)}
            style={{ marginLeft: 8 }}
          >
            Cancel
          </button>
        </form>
      ) : (
        <>
          <p>
            <strong>Name:</strong> {project.name}
          </p>
          <p>
            <strong>Email:</strong> {project.email}
          </p>
          <p>
            <strong>Phone:</strong> {project.phone}
          </p>
          <p>
            <strong>Description:</strong> {project.description}
          </p>
          {project.imageURLs && project.imageURLs.length > 0 && (
            <div style={{ margin: "1rem 0" }}>
              <strong>Images:</strong>
              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                {project.imageURLs.map((url, i) => (
                  <a
                    href={url}
                    key={i}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={url}
                      alt={`project-upload-${i}`}
                      style={{
                        width: 80,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 4,
                        border: "1px solid #eee",
                      }}
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <hr style={{ margin: "2rem 0" }} />
      <h2>Project Chat</h2>
      <div className="chat-history">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`chat-message ${msg.sender === "admin" ? "admin" : "user"}`}
          >
            <span className="chat-sender">{msg.sender}:</span>
            <span className="chat-text">{msg.text}</span>
            <span className="chat-timestamp">
              {msg.timestamp?.toDate
                ? msg.timestamp.toDate().toLocaleString()
                : ""}
            </span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      {isOwner && ( // userIsAdmin = your logic for admin detection if you have it
        <form className="chat-input-form" onSubmit={sendMessage}>
          <input
            type="text"
            placeholder="Type your message…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="chat-input"
          />
          <button type="submit" className="chat-send-btn">
            Send
          </button>
        </form>
      )}
    </div>
  );
}

export default ProjectPage;
